import * as THREE from 'three';
import { C, OreKind } from '../game/constants';

interface P {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  life: number;
  maxLife: number;
  gravity: number;
  spin: number;
}

interface Drop {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  kind: OreKind;
  life: number;
}

/** Pooled debris/spark particles + magnetic ore drops. */
export class Particles {
  readonly group = new THREE.Group();
  private readonly pool: P[] = [];
  private readonly active: P[] = [];
  readonly drops: Drop[] = [];
  private readonly boxGeo = new THREE.BoxGeometry(0.16, 0.16, 0.16);
  private readonly oreGeo = new THREE.OctahedronGeometry(0.26);

  private obtain(color: number, size: number): P {
    let p = this.pool.pop();
    if (!p) {
      const mesh = new THREE.Mesh(
        this.boxGeo,
        new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true }),
      );
      p = { mesh, vel: new THREE.Vector3(), life: 0, maxLife: 1, gravity: -20, spin: 0 };
      this.group.add(mesh);
    }
    (p.mesh.material as THREE.MeshBasicMaterial).color.set(color);
    (p.mesh.material as THREE.MeshBasicMaterial).opacity = 1;
    p.mesh.scale.setScalar(size);
    p.mesh.visible = true;
    this.active.push(p);
    return p;
  }

  burst(
    at: THREE.Vector3, color: number,
    n = 8, speed = 6, gravity = -20, life = 0.6, size = 1,
  ): void {
    for (let i = 0; i < n; i++) {
      const p = this.obtain(color, size * (0.7 + Math.random() * 0.6));
      p.mesh.position.copy(at);
      const a = Math.random() * Math.PI * 2;
      const s = speed * (0.4 + Math.random() * 0.8);
      p.vel.set(Math.cos(a) * s, Math.sin(a) * s * 0.9 + speed * 0.3, (Math.random() - 0.5) * 2);
      p.life = p.maxLife = life * (0.6 + Math.random() * 0.8);
      p.gravity = gravity;
      p.spin = (Math.random() - 0.5) * 10;
    }
  }

  spawnDrop(at: THREE.Vector3, kind: OreKind): void {
    const mesh = new THREE.Mesh(
      this.oreGeo,
      new THREE.MeshBasicMaterial({
        color: C.ore[kind].glow,
        transparent: true, opacity: 0.95,
        blending: THREE.AdditiveBlending, depthWrite: false,
      }),
    );
    mesh.position.copy(at);
    this.group.add(mesh);
    this.drops.push({
      mesh,
      vel: new THREE.Vector3((Math.random() - 0.5) * 5, 4 + Math.random() * 3, 0),
      kind,
      life: 240,   // pieces persist — go back for what you dropped
    });
  }

  /** Remove all floating drops (e.g. when leaving a planet behind). */
  clearDrops(): void {
    for (const d of this.drops) this.group.remove(d.mesh);
    this.drops.length = 0;
  }

  /** Update; drops magnet toward `attract` and report collection. */
  update(
    dt: number,
    attract: THREE.Vector3 | null,
    onCollect: (kind: OreKind) => boolean,
    solidAt: (x: number, y: number) => boolean,
  ): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const p = this.active[i];
      p.life -= dt;
      if (p.life <= 0) {
        p.mesh.visible = false;
        this.active.splice(i, 1);
        this.pool.push(p);
        continue;
      }
      p.vel.y += p.gravity * dt;
      p.mesh.position.addScaledVector(p.vel, dt);
      p.mesh.rotation.z += p.spin * dt;
      (p.mesh.material as THREE.MeshBasicMaterial).opacity = p.life / p.maxLife;
    }

    for (let i = this.drops.length - 1; i >= 0; i--) {
      const d = this.drops[i];
      d.life -= dt;
      d.mesh.rotation.y += dt * 3;

      // Free-floating pieces: you RETRIEVE them — walk right up to grab.
      // Only the faintest nudge inside arm's reach, no vacuum.
      if (attract) {
        const to = new THREE.Vector3().subVectors(attract, d.mesh.position);
        const dist = to.length();
        if (dist < 1.15) {
          if (onCollect(d.kind)) {
            this.group.remove(d.mesh);
            this.drops.splice(i, 1);
            continue;
          }
        } else if (dist < 2.0) {
          d.vel.addScaledVector(to.normalize(), 10 * dt);
        }
      }

      // Settle with a soft residual float + idle bob once slow
      d.vel.y -= 10 * dt;
      d.vel.multiplyScalar(Math.pow(0.92, dt * 60));
      const bob = Math.sin(performance.now() / 500 + i) * 0.15;
      const nx = d.mesh.position.x + d.vel.x * dt;
      const ny = d.mesh.position.y + (d.vel.y + bob) * dt;
      if (!solidAt(nx, d.mesh.position.y)) d.mesh.position.x = nx;
      if (!solidAt(d.mesh.position.x, ny)) d.mesh.position.y = ny;
      else d.vel.y = Math.abs(d.vel.y) * 0.3;   // soft bounce, keeps floating

      const pulse = 1 + Math.sin(performance.now() / 300 + i * 2) * 0.12;
      d.mesh.scale.setScalar(pulse);

      if (d.life <= 0) {
        this.group.remove(d.mesh);
        this.drops.splice(i, 1);
      }
    }
  }
}
